import React, { memo, useEffect } from 'react';
import {
  TransformComponent,
  TransformWrapper
} from 'react-zoom-pan-pinch/dist';
import { CmsAsset } from '../../integrations/contentful/types';
import { usePrevious } from '../../hooks/usePrevious';
import {
  isImageContent,
  isVideoContent
} from '../../integrations/contentful/utils';
import CmsVideo from '../cms/CmsVideo';
import { baseXUnit, breakpoints } from '../../styles/theme';
import CmsImage from '../cms/CmsImage';

export const mediaGalleryModalCaptionDesktopHeightBase = 12;
export const mediaGalleryModalSlideLeftPaddingBase = 3;

const desktopMediaPaddingBase = {
  bottom: baseXUnit(mediaGalleryModalCaptionDesktopHeightBase),
  left: baseXUnit(mediaGalleryModalSlideLeftPaddingBase)
};

const MediaGallerySlideVideo: React.FunctionComponent<{ video: CmsAsset }> = ({
  video
}) => (
  <div className="video-wrapper" data-testid="video-wrapper">
    <CmsVideo video={video} fill skeleton={false} />
    <style jsx>{`
      .video-wrapper {
        width: 100%;
        height: 100%;
      }

      @media ${breakpoints.medium} {
        .video-wrapper {
          padding-bottom: ${desktopMediaPaddingBase.bottom};
          padding-left: ${desktopMediaPaddingBase.left};
        }
      }
    `}</style>
  </div>
);

interface TransformWrapperRenderProps {
  scale: number;
  resetTransform: () => void;
}

// "react-zoom-pan-pinch" only offers a render function for the "resetTransform" -method and hooks cannot be used
// within render function callbacks. That is why we need a separate "TransformReset" function that tracks the changes
// in the props and calls reset, if necessary.
const TransformReset: React.FunctionComponent<{
  resetTransform: () => void;
  scale: number;
  isCurrentSlide?: boolean;
  isZoomed?: boolean;
}> = ({ resetTransform, isCurrentSlide, scale, isZoomed, children }) => {
  const wasCurrentSlide = usePrevious(isCurrentSlide);
  const slideChanged = isCurrentSlide !== wasCurrentSlide;

  useEffect(() => {
    // This reset is needed, when the slide was previously zoomed/panned and we return the slide.
    if (isCurrentSlide && slideChanged && scale !== 1) {
      resetTransform();
    }
  }, [isCurrentSlide, slideChanged, resetTransform, scale]);

  const wasZoomed = usePrevious(isZoomed);
  const zoomStateChanged = isZoomed !== wasZoomed;
  useEffect(() => {
    // This reset is needed, when the user tries to zoom out in order to get to default zoom (scale === 1).
    // Because of a race-condition, sometimes panning is disabled before "react-zoom-pan-pinch" has had the time to
    // automatically reset the view. This causes the image to stay in a weird position.
    const panningDisabled = !isZoomed;
    if (isCurrentSlide && zoomStateChanged && panningDisabled && scale !== 1) {
      resetTransform();
    }
  }, [isCurrentSlide, zoomStateChanged, isZoomed, resetTransform, scale]);

  return <div>{children}</div>;
};

interface MediaGallerySlideProps {
  isCurrentSlide?: boolean;
  isZoomed?: boolean;
  onZoomChange: (zoomInfo: { scale: number }) => void;
  media: CmsAsset;
}

const MediaGallerySlideImage: React.FunctionComponent<MediaGallerySlideProps> =
  ({ isCurrentSlide, isZoomed, onZoomChange, media }) => (
    <div className="media-gallery-slide-image">
      <TransformWrapper
        options={{ disabled: !isCurrentSlide }}
        pan={{ disabled: !isZoomed }}
        wheel={{ disabled: true }}
        onPinchingStop={onZoomChange}
      >
        {({ scale, resetTransform }: TransformWrapperRenderProps) => {
          return (
            <TransformComponent>
              <TransformReset
                scale={scale}
                resetTransform={resetTransform}
                isCurrentSlide={isCurrentSlide}
                isZoomed={isZoomed}
              >
                <div className="image-padder">
                  <CmsImage
                    image={media}
                    width={1920}
                    height={1920}
                    forceJpgOrWebp
                    skeleton={false}
                  />
                </div>
              </TransformReset>
            </TransformComponent>
          );
        }}
      </TransformWrapper>
      {/*language=CSS*/}
      <style jsx>{`
        .media-gallery-slide-image {
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        /* Pinch to zoom area needs to fill the whole screen and the content should be centered, so we stretch all divs */
        .media-gallery-slide-image :global(div) {
          height: 100%;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .media-gallery-slide-image :global(img) {
          max-width: 100%;
          max-height: 100%;
          height: auto;
          width: auto;
        }

        @media ${breakpoints.medium} {
          .image-padder {
            padding-bottom: ${desktopMediaPaddingBase.bottom};
            padding-left: ${desktopMediaPaddingBase.left};
          }
        }
      `}</style>
    </div>
  );

const MediaGallerySlide: React.FunctionComponent<MediaGallerySlideProps> = ({
  isCurrentSlide,
  isZoomed,
  onZoomChange,
  media
}) => {
  let content = <div />;

  if (isVideoContent(media.fields.file.contentType)) {
    content = <MediaGallerySlideVideo video={media} />;
  }
  if (isImageContent(media.fields.file.contentType)) {
    content = (
      <MediaGallerySlideImage
        media={media}
        isCurrentSlide={isCurrentSlide}
        isZoomed={isZoomed}
        onZoomChange={onZoomChange}
      />
    );
  }
  return (
    <div className="media-gallery-slide" data-testid="media-gallery-slide">
      {content}
      {/*language=CSS*/}
      <style jsx>{`
        .media-gallery-slide {
          height: 100%;
          display: flex;
          align-items: center;
        }
      `}</style>
    </div>
  );
};

export default memo(MediaGallerySlide);
